// Libs
import classNames from 'classnames/bind';
import { Button, FileDrop, Flex, IconEndLine, Img, Modal, Heading, View } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { isAxiosError } from 'axios';
// Components, Layouts, Pages
// Others
import { FileTypeExtEnum } from '~/utils/enum/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { updateProfile } from '~/thunks/user/userThunk';
import { convertBase64ToFile, generateGuid } from '~/utils/helper/common';
import { userActions } from '~/thunks/user/userSlice';
import { TIMEOUT_ALERT_MILLISECOND } from '~/utils/constants/common';
import { toastActions } from '~/thunks/toast/toastSlice';
import { IToast } from '~/utils/interface/toast';
// Styles, images, icons
import styles from './ModalUploadAvatar.module.scss';
import { icons } from '~/assets';

type Props = {
  isModalOpen: boolean;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const ModalUploadAvatar = (props: Props) => {
  //#region Destructuring Props
  const { isModalOpen, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const isRefresh = useAppSelector((state) => state.userState.isRefresh);
  //#endregion Selector

  //#region Declare State
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.5);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function

  const handleImageUpload = (acceptedFiles: ArrayLike<File | DataTransferItem>) => {
    const filesArray = Array.from(acceptedFiles)
      .map((item) => (item instanceof File ? item : item.kind === 'file' ? item.getAsFile() : null))
      .filter((file): file is File => file !== null);

    const file = filesArray[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target?.result) return;

      const img = new Image();
      img.src = e.target.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        canvas.width = 200;
        canvas.height = 200;

        const scale = Math.max(canvas.width / img.width, canvas.height / img.height);

        const newWidth = img.width * scale;
        const newHeight = img.height * scale;

        const x = (canvas.width - newWidth) / 2;
        const y = (canvas.height - newHeight) / 2;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(img, x, y, newWidth, newHeight);

        setSelectedImage(canvas.toDataURL(FileTypeExtEnum.PNG));
      };
    };

    reader.readAsDataURL(file);
  };

  const handleCloseModal = () => {
    onClose();
    setSelectedImage(null);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
  };

  const handleSave = async () => {
    if (selectedImage && croppedAreaPixels) {
      const croppedImage = await getCroppedImage(selectedImage, croppedAreaPixels);
      if (croppedImage) {
        handleUpdateProfile(croppedImage);
      }
    }
  };

  const handleUpdateProfile = async (avatar: string) => {
    const formData = new FormData();
    const file = convertBase64ToFile(avatar);

    if (file) {
      formData.append('profileImage', file);
    }

    try {
      setIsUpdating(true);
      await dispatch(updateProfile(formData)).unwrap();
      handleCloseModal();
      dispatch(userActions.setRefresh(!isRefresh));
    } catch (error) {
      if (isAxiosError(error)) {
        const messageId = generateGuid();
        const toast: IToast = {
          id: messageId,
          message: error.response?.data?.message,
          type: 'error',
        };

        dispatch(toastActions.addToast(toast));
        setTimeout(() => {
          dispatch(toastActions.removeToast(messageId));
        }, TIMEOUT_ALERT_MILLISECOND);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getCroppedImage = async (imageSrc: string, pixelCrop: Area) => {
    const img = new Image();
    img.src = imageSrc;

    await new Promise((resolve) => (img.onload = resolve));

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      img,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return canvas.toDataURL(FileTypeExtEnum.PNG);
  };

  //#endregion Handle Function

  return (
    <Modal open={isModalOpen} label='Upload Avatar' size='medium'>
      <Modal.Header frameBorder={'none'}>
        <View className={cx('buttonClose')}>
          <View as={'div'} onClick={handleCloseModal} className={cx('iconCloseModal')}>
            <IconEndLine />
          </View>
        </View>
      </Modal.Header>
      <Modal.Body>
        {selectedImage ? (
          <View as='div' textAlign='center' className={cx('imageWrapper')}>
            <View as='div' className={cx('imageCropper')}>
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={handleCropComplete}
                cropShape='round'
                onZoomChange={setZoom}
              />
            </View>
          </View>
        ) : (
          <FileDrop
            height={'300px'}
            onDropAccepted={handleImageUpload}
            renderLabel={
              <Flex
                direction='column'
                padding='0 small 0 small'
                justifyItems='center'
                alignItems='center'
                height={'100%'}
              >
                <Img src={icons.commonIconImageDefault} width={150} height={150} />
                <Heading level='h3' margin='0 0 small 0'>
                  {t('upload_image_title_label', { ns: 'common/base' })}
                </Heading>
              </Flex>
            }
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Flex justifyItems='end' gap='xx-small'>
          <Button color='primary-inverse' onClick={handleCloseModal}>
            {t('cancel_button_label', { ns: 'common/base' })}
          </Button>

          <Button color='primary' onClick={handleSave} disabled={!selectedImage || isUpdating}>
            {isUpdating
              ? t('saving_button_label', { ns: 'common/base' })
              : t('save_button_label', { ns: 'common/base' })}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalUploadAvatar;
